import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Heart, ChatCircle, Share, BookmarkSimple, DotsThree, Flag, PencilSimple, Trash,
    MapPin, Fire, Airplane, HandsClapping, Sparkle, CaretLeft, CaretRight, SpeakerHigh, SpeakerSlash,
    Suitcase, Compass, ArrowRight, Spinner, Play, Pause, PaperPlaneTilt
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { postsApi, reportsApi, reelsApi } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import FollowButton from "./FollowButton";
import FriendButton from "./FriendButton";
import TripCountdown from "./TripCountdown";
import ReelModal from "./ReelModal";
import { useTranslation } from "react-i18next";
import { usePassportShare } from "@/components/messages/PassportShareProvider";

const REACTIONS = [
    { key: "love", Icon: Heart, label: "Love", color: "#ff5f5f" },
    { key: "amazing", Icon: Fire, label: "Amazing", color: "#ff9d4d" },
    { key: "beautiful", Icon: Sparkle, label: "Beautiful", color: "#d4af37" },
    { key: "travel", Icon: Airplane, label: "Travel", color: "#7cc4ff" },
    { key: "great", Icon: HandsClapping, label: "Great", color: "#c8a2ff" },
];

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.round((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.round(h / 24)}d`;
}

function renderCaption(text) {
    if (!text) return null;
    const parts = text.split(/(\s+)/);
    return parts.map((p, i) => {
        if (/^#([A-Za-z0-9_]{1,40})$/.test(p)) {
            const tag = p.slice(1);
            return (
                <Link key={i} to={`/hashtag/${tag}`} className="text-[color:var(--gold)] hover:underline">
                    {p}
                </Link>
            );
        }
        if (/^@([a-z0-9_.]{2,30})$/.test(p)) {
            return <span key={i} className="text-[color:var(--gold)]">{p}</span>;
        }
        return <span key={i}>{p}</span>;
    });
}

function MediaCarousel({ media, poster, country, onDoubleTap, onOpenModal }) {
    const [i, setI] = useState(0);
    const [muted, setMuted] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [showIcon, setShowIcon] = useState(false);
    const videoRef = useRef(null);
    const imgRef = useRef(null);
    const iconTimer = useRef(null);

    useEffect(() => {
        setIsLoaded(false);
        setIsPlaying(false);
        setProgress(0);
        if (imgRef.current && imgRef.current.complete) setIsLoaded(true);
        if (videoRef.current && videoRef.current.readyState >= 2) setIsLoaded(true);
    }, [i, media]);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const handlePlay = (e) => {
            if (e.target !== v) {
                v.pause();
                setIsPlaying(false);
            }
        };
        document.addEventListener('play', handlePlay, true);
        return () => document.removeEventListener('play', handlePlay, true);
    }, [i]);

    const togglePlay = () => {
        const v = videoRef.current;
        if (!v) return;
        v.paused ? v.play() : v.pause();
        setShowIcon(true);
        clearTimeout(iconTimer.current);
        iconTimer.current = setTimeout(() => setShowIcon(false), 800);
    };

    const handleSeek = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const pos = (e.clientX - rect.left) / rect.width;
        const v = videoRef.current;
        if (v && v.duration) {
            v.currentTime = pos * v.duration;
            setProgress(pos * 100);
        }
    };

    if (!media || media.length === 0) {
        return poster ? (
            <div className="relative bg-black" style={{ height: 420 }}>
                <img src={poster} alt="" className="w-full h-full object-cover" onDoubleClick={onDoubleTap} />
            </div>
        ) : null;
    }
    const item = media[i];
    return (
        <div className="relative bg-black" style={{ height: 420 }} data-testid="post-media">
            {!isLoaded && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                    <Spinner size={32} color="var(--gold)" className="animate-spin" />
                </div>
            )}
            {item.kind === "video" ? (
                <div 
                    className="relative w-full h-full"
                    onMouseEnter={() => videoRef.current?.play().catch(() => {})}
                    onMouseLeave={() => { videoRef.current?.pause(); if (videoRef.current) videoRef.current.currentTime = 0; }}
                >
                    <video
                        ref={videoRef}
                        src={item.url}
                        poster={poster}
                        className="w-full h-full block"
                        style={{ objectFit: "cover", opacity: isLoaded ? 1 : 0, transition: "opacity 0.3s" }}
                        muted={muted}
                        playsInline
                        preload="metadata"
                        loop
                        onLoadedData={() => setIsLoaded(true)}
                        onError={() => setIsLoaded(true)}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onTimeUpdate={(e) => {
                            const v = e.target;
                            if (v.duration) setProgress((v.currentTime / v.duration) * 100);
                        }}
                        onDoubleClick={onDoubleTap}
                    />
                    <div
                        className="absolute inset-0 cursor-pointer"
                        style={{ zIndex: 5 }}
                        onClick={onOpenModal || togglePlay}
                    />
                    {showIcon && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 6 }}>
                            <div
                                className="w-16 h-16 rounded-full flex items-center justify-center"
                                style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
                            >
                                {isPlaying
                                    ? <Pause size={28} color="white" weight="fill" />
                                    : <Play size={28} color="white" weight="fill" style={{ marginLeft: 3 }} />}
                            </div>
                        </div>
                    )}
                    <div 
                        className="absolute bottom-0 left-0 right-0 cursor-pointer flex items-end group" 
                        style={{ height: 24, zIndex: 7 }}
                        onPointerDown={handleSeek}
                    >
                        <div className="w-full" style={{ height: 4, background: "rgba(255,255,255,0.15)" }}>
                            <div style={{ width: `${progress}%`, height: "100%", background: "var(--gold)", transition: "width 0.1s linear" }} />
                        </div>
                    </div>
                    <button
                        onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }}
                        className="absolute bottom-5 right-3 w-9 h-9 rounded-full grid place-items-center"
                        style={{ background: "rgba(0,0,0,0.6)", color: "white", zIndex: 8 }}
                        data-testid="video-mute-toggle"
                    >
                        {muted ? <SpeakerSlash size={14} /> : <SpeakerHigh size={14} />}
                    </button>
                </div>
            ) : (
                <img
                    ref={imgRef}
                    src={item.url}
                    alt=""
                    className="w-full h-full object-cover block cursor-pointer"
                    style={{ opacity: isLoaded ? 1 : 0, transition: "opacity 0.3s" }}
                    loading="lazy"
                    onLoad={() => setIsLoaded(true)}
                    onError={() => setIsLoaded(true)}
                    onDoubleClick={onDoubleTap}
                    onClick={onOpenModal}
                />
            )}
            {media.length > 1 && (
                <>
                    <button
                        aria-label="Prev"
                        onClick={(e) => { e.stopPropagation(); setI((n) => Math.max(0, n - 1)); }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full grid place-items-center"
                        style={{ background: "rgba(0,0,0,0.5)", color: "white", zIndex: 9 }}
                        disabled={i === 0}
                        data-testid="carousel-prev"
                    >
                        <CaretLeft size={16} weight="bold" />
                    </button>
                    <button
                        aria-label="Next"
                        onClick={(e) => { e.stopPropagation(); setI((n) => Math.min(media.length - 1, n + 1)); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full grid place-items-center"
                        style={{ background: "rgba(0,0,0,0.5)", color: "white", zIndex: 9 }}
                        disabled={i === media.length - 1}
                        data-testid="carousel-next"
                    >
                        <CaretRight size={16} weight="bold" />
                    </button>
                    <div className="absolute top-3 right-3 text-xs px-2 py-1 rounded-full" style={{ background: "rgba(0,0,0,0.5)", color: "white", zIndex: 9 }}>
                        {i + 1}/{media.length}
                    </div>
                </>
            )}
        </div>
    );
}

export default function PostCard({ post, onOpenComments, onChanged, onDeleted, onUpdate }) {
    const { user } = useAuth();
    const { t } = useTranslation();
    const { openPassportShare } = usePassportShare();
    const local = post;
    const [showReactions, setShowReactions] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const isMine = user && local.user_id === user.id;
    const media = local.media && local.media.length ? local.media : (local.image_url ? [{ url: local.image_url, kind: local.is_reel ? "video" : "image", mime: local.is_reel ? "video/mp4" : "image/jpeg" }] : []);
    const total = local.reactions_total ?? local.likes ?? 0;
    const myReaction = local.my_reaction || null;

    const react = async (type) => {
        if (!user) return toast.error(t("postCard.toasts.signInToReact"));
        const prev = local.my_reaction;
        const nextTotal = total + (prev ? (prev === type ? -1 : 0) : 1);
        const nextReaction = prev === type ? null : type;
        if (onUpdate) onUpdate({ my_reaction: nextReaction, reactions_total: nextTotal, likes: nextTotal });
        setShowReactions(false);
        try {
            if (local.is_reel && local.reel_id) {
                await reelsApi.like(local.reel_id);
            } else {
                await postsApi.react(local.id, type);
            }
        } catch {
            if (onUpdate) onUpdate({ my_reaction: prev, reactions_total: total, likes: total });
            toast.error(t("postCard.toasts.reactFailed"));
        }
    };

    const toggleSave = async () => {
        if (!user) return toast.error(t("postCard.toasts.signInToSave"));
        try {
            if (local.saved) {
                await postsApi.unsave(local.id);
                if (onUpdate) onUpdate({ saved: false });
                toast.success(t("postCard.toasts.removedSave"));
            } else {
                await postsApi.save(local.id);
                if (onUpdate) onUpdate({ saved: true });
                toast.success(t("postCard.toasts.saved"));
            }
        } catch {
            toast.error(t("postCard.toasts.saveFailed"));
        }
    };

    const share = async () => {
        try {
            let url;
            // If this feed post is a reel proxy, use the reel share API so the
            // reel's own counter is incremented and we get the canonical reel URL.
            if (local.is_reel && local.reel_id) {
                const r = await reelsApi.share(local.reel_id);
                url = `${window.location.origin}${r.path}`;
            } else {
                const r = await postsApi.share(local.id);
                url = `${window.location.origin}/p/${r.slug}`;
            }
            if (navigator.share) {
                await navigator.share({ title: "Passport", text: local.caption?.slice(0, 100), url });
            } else {
                await navigator.clipboard.writeText(url);
                toast.success(t("postCard.toasts.copied"));
            }
            // Update share count in local state so feed/saved sections stay in sync
            if (onUpdate) onUpdate({ shares: (local.shares || 0) + 1 });
        } catch {
            toast.error(t("postCard.toasts.shareFailed"));
        }
    };

    const shareInMessages = () => {
        openPassportShare({
            shareRef: {
                entity_type: local.is_reel && local.reel_id ? "reel" : "post",
                entity_id: local.is_reel && local.reel_id ? local.reel_id : local.id,
            },
            sourceLabel: local.is_reel ? "this Reel" : "this Post",
        });
    };

    const del = async () => {
        setDeleted(true);
        setMenuOpen(false);
        setConfirmDelete(false);
        try {
            await postsApi.remove(local.id);
            toast.success(t("postCard.toasts.deleted"));
            onDeleted?.(local.id);
        } catch {
            setDeleted(false);
            toast.error(t("postCard.toasts.deleteFailed"));
        }
    };

    const primaryReaction = myReaction || "love";
    const PrimaryIcon = (REACTIONS.find((r) => r.key === primaryReaction) || REACTIONS[0]).Icon;
    const primaryColor = (REACTIONS.find((r) => r.key === primaryReaction) || REACTIONS[0]).color;

    if (deleted) return null;

    return (
        <motion.article
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            className="rounded-3xl overflow-hidden break-inside-avoid"
            style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}
            data-testid={`post-${local.id}`}
        >
            <div className="p-4 flex items-center gap-3">
                <Link to={`/profile/${local.user_handle}`} className="shrink-0">
                    <img
                        src={local.user_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${local.user_handle}`}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover"
                        style={{ border: "1px solid rgba(212,175,55,0.4)" }}
                    />
                </Link>
                <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium truncate">{local.user_name}</div>
                    <div className="text-[11px] text-white/50 flex items-center gap-2">
                        <span style={{ color: "var(--gold)" }}>@{local.user_handle}</span>
                        <span>·</span>
                        <span>{timeAgo(local.created_at)}</span>
                        {local.privacy && local.privacy !== "public" && (
                            <span className="uppercase tracking-widest text-[9px] px-1.5 py-0.5 rounded" style={{ background: "rgba(212,175,55,0.15)", color: "var(--gold)" }}>
                                {local.privacy}
                            </span>
                        )}
                    </div>
                </div>
                {!isMine && user && <FollowButton handle={local.user_handle} />}
                <div className="relative">
                    <button onClick={() => setMenuOpen((v) => !v)} aria-label="More" className="w-8 h-8 rounded-full grid place-items-center hover:bg-white/5" data-testid={`post-menu-${local.id}`}>
                        <DotsThree size={20} weight="bold" />
                    </button>
                    <AnimatePresence>
                        {menuOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="absolute right-0 top-10 z-30 rounded-xl p-1 min-w-[180px]"
                                style={{ background: "var(--navy)", border: "1px solid rgba(255,255,255,0.08)" }}
                                onMouseLeave={() => setMenuOpen(false)}
                            >
                                {isMine ? (
                                    <>
                                        <button onClick={() => { setMenuOpen(false); shareInMessages(); }} className="w-full text-left text-sm px-3 py-2 rounded-lg hover:bg-white/5 flex items-center gap-2" data-testid={`post-send-menu-${local.id}`}>
                                            <PaperPlaneTilt size={14} /> Send in Passport Messages
                                        </button>
                                        <button onClick={() => { setMenuOpen(false); setEditOpen(true); }} className="w-full text-left text-sm px-3 py-2 rounded-lg hover:bg-white/5 flex items-center gap-2" data-testid={`post-edit-${local.id}`}>
                                            <PencilSimple size={14} /> {t("common.edit")}
                                        </button>
                                        {!confirmDelete ? (
                                            <button onClick={() => setConfirmDelete(true)} className="w-full text-left text-sm px-3 py-2 rounded-lg hover:bg-white/5 flex items-center gap-2 text-red-300" data-testid={`post-delete-${local.id}`}>
                                                <Trash size={14} /> {t("common.delete")}
                                            </button>
                                        ) : (
                                            <div className="px-3 py-2 flex items-center gap-2">
                                                <span className="text-xs text-white/60 flex-1">{t("postCard.deleteConfirm")}</span>
                                                <button onClick={del} className="text-xs px-2 py-1 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/40 transition-colors" data-testid={`post-delete-confirm-${local.id}`}>Yes</button>
                                                <button onClick={() => setConfirmDelete(false)} className="text-xs px-2 py-1 rounded-lg hover:bg-white/10 text-white/60 transition-colors">No</button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => { setMenuOpen(false); shareInMessages(); }} className="w-full text-left text-sm px-3 py-2 rounded-lg hover:bg-white/5 flex items-center gap-2" data-testid={`post-send-menu-${local.id}`}>
                                            <PaperPlaneTilt size={14} /> Send in Passport Messages
                                        </button>
                                        <button onClick={() => { setMenuOpen(false); setReportOpen(true); }} className="w-full text-left text-sm px-3 py-2 rounded-lg hover:bg-white/5 flex items-center gap-2" data-testid={`post-report-${local.id}`}>
                                            <Flag size={14} /> {t("post.report")}
                                        </button>
                                    </>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {media.length > 0 && <MediaCarousel media={media} country={local.country} onDoubleTap={() => react("love")} onOpenModal={() => setModalOpen(true)} />}

            {media.length === 0 && local.country && (
                <div className="px-4 pt-4">
                    <div
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold tracking-wider uppercase"
                        style={{ background: "rgba(212,175,55,0.14)", color: "var(--gold)", border: "1px solid rgba(212,175,55,0.35)" }}
                        data-testid="post-country-pill"
                    >
                        <Airplane size={10} weight="fill" color="#D4AF37" />
                        {local.country}
                    </div>
                </div>
            )}

            {local.preview && local.preview.type === "itinerary" && (
                <ItineraryPreviewCard preview={local.preview} />
            )}

            <div className="p-4 space-y-3">
                {local.caption && <div className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap">{renderCaption(local.caption)}</div>}
                {(local.location || local.country) && (
                    <Link to={local.country ? `/hashtag/${(local.country || "").replace(/\s+/g, "").toLowerCase()}` : "#"} className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors">
                        <MapPin size={12} weight="fill" color="#D4AF37" />
                        <span>{[local.location, local.country].filter(Boolean).join(", ")}</span>
                    </Link>
                )}
                {local.tips && (
                    <div className="text-xs italic text-white/60 border-l-2 pl-3" style={{ borderColor: "var(--gold)" }}>
                        Tip · {local.tips}
                    </div>
                )}

                <div className="flex items-center gap-1 pt-1">
                    <div className="relative">
                        <button
                            onClick={() => react(myReaction || "love")}
                            onMouseEnter={() => { if (!local.is_reel) setShowReactions(true); }}
                            onMouseLeave={() => { if (!local.is_reel) setShowReactions(false); }}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm hover:bg-white/5 transition-colors"
                            data-testid={`post-react-${local.id}`}
                        >
                            <PrimaryIcon size={16} weight={myReaction ? "fill" : "regular"} color={myReaction ? primaryColor : "white"} />
                            <span style={{ color: myReaction ? primaryColor : "rgba(255,255,255,0.7)" }}>{total}</span>
                        </button>
                        <AnimatePresence>
                            {showReactions && !local.is_reel && (
                                <motion.div
                                    initial={{ opacity: 0, y: 6, scale: 0.9 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 6, scale: 0.9 }}
                                    onMouseEnter={() => setShowReactions(true)}
                                    onMouseLeave={() => setShowReactions(false)}
                                    className="absolute bottom-full left-0 mb-2 flex gap-1 p-1.5 rounded-full z-20"
                                    style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.3)" }}
                                    data-testid={`post-reactions-menu-${local.id}`}
                                >
                                    {REACTIONS.map(({ key, Icon, color, label }) => (
                                        <button key={key} onClick={() => react(key)} title={label} aria-label={label} className="w-9 h-9 rounded-full grid place-items-center hover:bg-white/10 transition-colors" data-testid={`react-${key}-${local.id}`}>
                                            <Icon size={18} weight={myReaction === key ? "fill" : "regular"} color={color} />
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <button onClick={() => onOpenComments?.(local)} className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm hover:bg-white/5 transition-colors" data-testid={`post-comment-btn-${local.id}`}>
                        <ChatCircle size={16} />
                        <span className="text-white/70">{local.comments}</span>
                    </button>

                    <button onClick={share} className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm hover:bg-white/5 transition-colors" data-testid={`post-share-${local.id}`}>
                        <Share size={16} />
                        <span className="text-white/70 hidden sm:inline">{t("common.share")}</span>
                    </button>

                    <button onClick={shareInMessages} className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm hover:bg-white/5 transition-colors" data-testid={`post-send-${local.id}`}>
                        <PaperPlaneTilt size={16} />
                        <span className="text-white/70 hidden sm:inline">Send</span>
                    </button>

                    <button onClick={toggleSave} className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-full text-sm hover:bg-white/5 transition-colors" data-testid={`post-save-${local.id}`}>
                        <BookmarkSimple size={16} weight={local.saved ? "fill" : "regular"} color={local.saved ? "#D4AF37" : "white"} />
                    </button>
                </div>
            </div>

            <ReportModal open={reportOpen} onClose={() => setReportOpen(false)} targetType="post" targetId={local.id} />
            <EditPostModal
                open={editOpen}
                post={local}
                onClose={() => setEditOpen(false)}
                onSaved={(updated) => { if (onUpdate) onUpdate(updated); setEditOpen(false); onChanged?.(updated); }}
            />

            <AnimatePresence>
                {modalOpen && (
                    <ReelModal
                        reel={{
                            is_post: true,
                            id: local.reel_id || local.id,
                            user_id: local.user_id,
                            user_handle: local.user_handle,
                            user_name: local.user_name,
                            user_avatar: local.user_avatar,
                            caption: local.caption,
                            location: local.location,
                            country: local.country,
                            media_url: media[0]?.url,
                            media: media,
                            poster_url: null,
                            kind: media[0]?.kind || 'image',
                            liked: !!myReaction,
                            likes: total,
                            comments_count: local.comments,
                            saves: local.saves || 0,
                            saved: local.saved,
                            shares: local.shares || 0,
                            views: local.views ?? 0,
                        }}
                        user={user}
                        onClose={() => setModalOpen(false)}
                        onLike={() => react("love")}
                        onSave={toggleSave}
                        onShare={shareInMessages}
                        onOpenComments={() => { setModalOpen(false); onOpenComments?.(local); }}
                        onReport={() => setReportOpen(true)}
                    />
                )}
            </AnimatePresence>
        </motion.article>
    );
}

function EditPostModal({ open, post, onClose, onSaved }) {
    const { t } = useTranslation();
    const [caption, setCaption] = useState(post.caption || "");
    const [location, setLocation] = useState(post.location || "");
    const [country, setCountry] = useState(post.country || "");
    const [hashtags, setHashtags] = useState((post.hashtags || []).join(" "));
    const [privacy, setPrivacy] = useState(post.privacy || "public");
    const [tips, setTips] = useState(post.tips || "");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setCaption(post.caption || "");
        setLocation(post.location || "");
        setCountry(post.country || "");
        setHashtags((post.hashtags || []).join(" "));
        setPrivacy(post.privacy || "public");
        setTips(post.tips || "");
    }, [open, post]);

    if (!open) return null;
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const tags = hashtags.split(/[,\s]+/).map((s) => s.replace(/^#/, "").trim().toLowerCase()).filter(Boolean);
            const updated = await postsApi.update(post.id, {
                caption, location: location || null, country: country || null,
                hashtags: tags, privacy, tips: tips || null,
            });
            toast.success(t("postCard.toasts.updated"));
            onSaved?.(updated);
        } catch { toast.error(t("postCard.toasts.updateFailed")); }
        finally { setBusy(false); }
    };
    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[95] flex items-center justify-center p-4" style={{ background: "rgba(3,7,15,0.9)", backdropFilter: "blur(10px)" }} onClick={onClose} data-testid="edit-post-modal">
                <motion.form onClick={(e) => e.stopPropagation()} onSubmit={submit} initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="rounded-3xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.3)" }}>
                    <div className="overline mb-1">{t("postCard.editPost")}</div>
                    <div className="font-serif text-2xl text-white mb-4">{t("postCard.refineMoment")}</div>
                    <div className="space-y-3">
                        <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} placeholder={t("postCard.placeholders.caption")} className="w-full bg-black/30 border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--gold)]" style={{ borderColor: "rgba(255,255,255,0.1)" }} data-testid="edit-post-caption" />
                        <div className="grid grid-cols-2 gap-2">
                            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t("postCard.placeholders.location")} className="bg-black/30 border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--gold)]" style={{ borderColor: "rgba(255,255,255,0.1)" }} data-testid="edit-post-location" />
                            <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder={t("postCard.placeholders.country")} className="bg-black/30 border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--gold)]" style={{ borderColor: "rgba(255,255,255,0.1)" }} data-testid="edit-post-country" />
                        </div>
                        <input value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder={t("postCard.placeholders.hashtags")} className="w-full bg-black/30 border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--gold)]" style={{ borderColor: "rgba(255,255,255,0.1)" }} data-testid="edit-post-hashtags" />
                        <input value={tips} onChange={(e) => setTips(e.target.value)} placeholder={t("postCard.placeholders.tips")} className="w-full bg-black/30 border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--gold)]" style={{ borderColor: "rgba(255,255,255,0.1)" }} data-testid="edit-post-tips" />
                        <div className="grid grid-cols-4 gap-2">
                            {["public", "followers", "friends", "private"].map((k) => (
                                <button key={k} type="button" onClick={() => setPrivacy(k)} className="text-xs px-3 py-2 rounded-full capitalize" style={{ background: privacy === k ? "var(--gold)" : "rgba(255,255,255,0.04)", color: privacy === k ? "var(--navy)" : "white", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`edit-privacy-${k}`}>
                                    {t(`postCard.privacy.${k}`)}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex gap-2 mt-6">
                        <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">{t("common.cancel")}</button>
                        <button type="submit" disabled={busy} className="btn-gold flex-1 justify-center" data-testid="edit-post-save">{busy ? "…" : t("common.save")}</button>
                    </div>
                </motion.form>
            </motion.div>
        </AnimatePresence>
    );
}

function ItineraryPreviewCard({ preview }) {
    const { t } = useTranslation();
    const isTrip = preview.kind === "trip";
    const chips = (preview.activities || []).slice(0, 5);
    return (
        <Link
            to={`/itinerary/${preview.itinerary_id}`}
            className="block mx-4 my-3 relative overflow-hidden rounded-2xl group"
            style={{
                background: "linear-gradient(140deg, rgba(212,175,55,0.14), rgba(11,19,32,0.6) 55%)",
                border: "1px solid rgba(212,175,55,0.35)",
            }}
            data-testid={`itinerary-preview-${preview.itinerary_id}`}
        >
            <div className="absolute inset-0 pointer-events-none opacity-30" style={{ background: "radial-gradient(ellipse at top right, rgba(212,175,55,0.35), transparent 60%)" }} />
            <div className="relative p-5">
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full grid place-items-center" style={{ background: "rgba(212,175,55,0.2)", border: "1px solid rgba(212,175,55,0.5)" }}>
                        {isTrip ? <Suitcase size={14} color="#D4AF37" weight="fill" /> : <Heart size={14} color="#F4A28F" weight="fill" />}
                    </div>
                    <div className="overline" style={{ color: "var(--gold)" }}>
                        {isTrip ? t("postCard.preview.aiItinerary") : t("postCard.preview.travelDate")}
                    </div>
                    <div className="ml-auto text-[10px] text-white/40 uppercase tracking-widest">{t("postCard.preview.compose")}</div>
                </div>
                <div className="font-serif text-2xl md:text-3xl text-white leading-tight tracking-tight mb-1.5">
                    {preview.title || t("postCard.preview.untitled")}
                </div>
                {preview.snippet && (
                    <div className="text-sm text-white/60 leading-relaxed mb-3 line-clamp-2">
                        {preview.snippet}
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-white/60">
                    {preview.destination && (
                        <span className="inline-flex items-center gap-1">
                            <MapPin size={11} weight="fill" color="#D4AF37" />
                            {preview.destination}
                        </span>
                    )}
                    {isTrip && preview.days && (
                        <span className="inline-flex items-center gap-1">
                            <Compass size={11} color="#D4AF37" />
                            {t("postCard.preview.days", { count: preview.days })}
                        </span>
                    )}
                    {!isTrip && preview.duration_label && (
                        <span className="inline-flex items-center gap-1">
                            <Compass size={11} color="#D4AF37" />
                            {preview.duration_label}
                        </span>
                    )}
                    {(preview.budget || preview.budget_usd) && (
                        <span className="inline-flex items-center gap-1">
                            <Sparkle size={11} color="#D4AF37" />
                            {preview.budget ? preview.budget : `~$${preview.budget_usd}`}
                        </span>
                    )}
                    {preview.style && !isTrip && (
                        <span className="inline-flex items-center gap-1 capitalize">
                            <Heart size={11} color="#F4A28F" />
                            {preview.style}
                        </span>
                    )}
                </div>
                {chips.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {chips.map((a) => (
                            <span key={a} className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.65)" }}>
                                {a}
                            </span>
                        ))}
                    </div>
                )}
                {preview.trip_date && (
                    <div className="mt-4">
                        <TripCountdown tripDate={preview.trip_date} destination={preview.destination} />
                    </div>
                )}
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: "var(--gold)" }}>
                    {t("postCard.preview.viewFull")} <ArrowRight size={12} weight="bold" />
                </div>
            </div>
        </Link>
    );
}

export function ReportModal({ open, onClose, targetType, targetId }) {
    const { t } = useTranslation();
    const [category, setCategory] = useState("spam");
    const [detail, setDetail] = useState("");
    const [busy, setBusy] = useState(false);
    const CATS = [
        { k: "spam", l: t("postCard.reportModal.categories.spam") },
        { k: "harassment", l: t("postCard.reportModal.categories.harassment") },
        { k: "hate", l: t("postCard.reportModal.categories.hate") },
        { k: "sexual", l: t("postCard.reportModal.categories.sexual") },
        { k: "violence", l: t("postCard.reportModal.categories.violence") },
        { k: "scam", l: t("postCard.reportModal.categories.scam") },
        { k: "copyright", l: t("postCard.reportModal.categories.copyright") },
        { k: "other", l: t("postCard.reportModal.categories.other") },
    ];
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await reportsApi.submit({ target_type: targetType, target_id: targetId, category, detail });
            toast.success(t("postCard.reportModal.toasts.success"));
            onClose();
            setDetail("");
        } catch (err) {
            toast.error(t("postCard.reportModal.toasts.failed"));
        } finally { setBusy(false); }
    };
    if (!open) return null;
    return ReactDOM.createPortal(
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(5,10,17,0.85)", backdropFilter: "blur(8px)" }} onClick={onClose} data-testid="report-modal">
                <motion.form onClick={(e) => e.stopPropagation()} onSubmit={submit} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="rounded-3xl max-w-md w-full p-8" style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.3)" }}>
                    <div className="overline mb-2">{t("postCard.reportModal.overline", { type: targetType })}</div>
                    <h3 className="font-serif text-2xl text-white mb-6">{t("postCard.reportModal.title")}</h3>
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                        {CATS.map((c) => (
                            <label key={c.k} className="flex items-center gap-3 p-3 rounded-xl cursor-pointer hover:bg-white/5" data-testid={`report-cat-${c.k}`}>
                                <input type="radio" name="cat" value={c.k} checked={category === c.k} onChange={(e) => setCategory(e.target.value)} className="accent-[color:var(--gold)]" />
                                <span className="text-sm text-white/85">{c.l}</span>
                            </label>
                        ))}
                    </div>
                    <textarea value={detail} onChange={(e) => setDetail(e.target.value)} placeholder={t("postCard.reportModal.detailsPlaceholder")} rows={3} className="w-full mt-4 bg-black/30 border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--gold)]" style={{ borderColor: "rgba(255,255,255,0.1)" }} data-testid="report-detail" />
                    <div className="flex gap-2 mt-4">
                        <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">{t("common.cancel")}</button>
                        <button type="submit" disabled={busy} className="btn-gold flex-1 justify-center" data-testid="report-submit">{busy ? "…" : t("common.submit")}</button>
                    </div>
                </motion.form>
            </motion.div>
        </AnimatePresence>,
        document.body
    );
}
